import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { auth, db, storage } from '../firebase';
import { collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { useUserData } from '../hooks/useUserData';

const ACCEPTED_AUDIO_TYPES = 'audio/mpeg,audio/mp3,.mp3,audio/x-m4a,audio/m4a,audio/mp4,.m4a,audio/wav,.wav,audio/webm,.webm,audio/ogg,.ogg,audio/flac,.flac,audio/aac,.aac';

const createClientUploadId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
const createMetadataEntry = () => ({
  id: `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
  key: '',
  value: '',
});

const formatDuration = (seconds) => {
  const safeSeconds = Math.max(0, Math.floor(Number(seconds || 0)));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const secs = safeSeconds % 60;
  return `${hours ? `${hours} ժ ` : ''}${minutes ? `${minutes} ր ` : ''}${secs} վրկ`;
};

const formatBytes = (bytes) => {
  const value = Number(bytes || 0);
  if (value <= 0) return '0 MB';
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
};

const isTerminalUploadState = (item) => (
  item.phase === 'completed'
  || item.phase === 'failed'
  || item.phase === 'insufficient_credits'
);

const resolveUploadPresentation = (item) => {
  if (item.phase === 'insufficient_credits') {
    return {
      label: 'Ժամանակը բավարար չէ',
      color: 'error',
      helper: item.error || 'Այս ֆայլի համար ձեր հասանելի րոպեները բավարար չեն։',
      progressVariant: null,
      progressValue: 0,
    };
  }

  if (item.phase === 'failed' || item.recordingStatus === 'failed' || item.analysisStatus === 'failed') {
    return {
      label: 'Չհաջողվեց',
      color: 'error',
      helper: item.error || 'Ֆայլի վերբեռնումը կամ մշակումը ձախողվեց։',
      progressVariant: null,
      progressValue: 0,
    };
  }

  if (item.phase === 'uploading') {
    return {
      label: `Վերբեռնվում է ${Math.round(item.progress || 0)}%`,
      color: 'primary',
      helper: 'Ֆայլը հիմա վերբեռնվում է։',
      progressVariant: 'determinate',
      progressValue: item.progress || 0,
    };
  }

  if (item.recordingStatus === 'processing') {
    return {
      label: 'Վերծանվում է',
      color: 'warning',
      helper: 'Վերբեռնումն ավարտված է, հիմա ընթանում է վերծանումը։',
      progressVariant: 'indeterminate',
      progressValue: 0,
    };
  }

  if (item.recordingStatus === 'transcribed' && item.analysisStatus === 'processing') {
    return {
      label: 'Վերլուծվում է',
      color: 'primary',
      helper: 'Վերծանումն արդեն ավարտված է, հիմա աշխատում է AI ստուգումը։',
      progressVariant: 'indeterminate',
      progressValue: 0,
    };
  }

  if (item.recordingStatus === 'transcribed' && item.analysisStatus === 'pending') {
    return {
      label: 'Սպասում է վերլուծությանը',
      color: 'warning',
      helper: 'Վերծանումն ավարտված է, AI ստուգումը կսկսի մի քանի վայրկյանից։',
      progressVariant: 'indeterminate',
      progressValue: 0,
    };
  }

  if (item.phase === 'completed' || item.analysisStatus === 'completed') {
    return {
      label: 'Վերլուծությունն ավարտված է',
      color: 'success',
      helper: 'Ֆայլը ավելացվել է այս ստուգացուցակի արդյունքներին։',
      progressVariant: 'determinate',
      progressValue: 100,
    };
  }

  if (item.analysisStatus === 'processing') {
    return {
      label: 'Վերլուծվում է',
      color: 'primary',
      helper: 'AI ստուգացուցակը հիմա վերլուծում է այս ձայնագրությունը։',
      progressVariant: 'indeterminate',
      progressValue: 0,
    };
  }

  if (item.recordingStatus === 'transcribed' || item.analysisStatus === 'pending') {
    return {
      label: 'Սպասում է վերլուծությանը',
      color: 'warning',
      helper: 'Վերծանումն ավարտված է, AI ստուգումը դեռ չի սկսվել։',
      progressVariant: 'indeterminate',
      progressValue: 0,
    };
  }

  if (item.phase === 'uploaded' || item.recordingStatus === 'uploading') {
    return {
      label: 'Սպասում է վերծանմանը',
      color: 'info',
      helper: 'Վերբեռնումն ավարտված է, համակարգը պատրաստվում է սկսել վերծանումը։',
      progressVariant: 'indeterminate',
      progressValue: 0,
    };
  }

  return {
    label: 'Նախապատրաստվում է',
    color: 'default',
    helper: 'Ստուգում ենք ֆայլը և պատրաստում ենք վերբեռնումը։',
    progressVariant: 'indeterminate',
    progressValue: 0,
  };
};

async function detectAudioMimeType(file) {
  try {
    const buf = await file.slice(0, 12).arrayBuffer();
    const b = new Uint8Array(buf);
    // MP4 / M4A container: box-size (4 bytes) then "ftyp" at bytes 4-7
    if (b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) {
      return 'audio/x-m4a';
    }
    // OGG container
    if (b[0] === 0x4f && b[1] === 0x67 && b[2] === 0x67 && b[3] === 0x53) {
      return 'audio/ogg';
    }
    // MP3: ID3 tag header
    if (b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33) {
      return 'audio/mpeg';
    }
    // MP3: raw MPEG sync word (0xFF 0xEx or 0xFF 0xFx)
    if (b[0] === 0xff && (b[1] & 0xe0) === 0xe0) {
      return 'audio/mpeg';
    }
    // WAV: RIFF....WAVE
    if (b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46) {
      return 'audio/wav';
    }
    // FLAC
    if (b[0] === 0x66 && b[1] === 0x4c && b[2] === 0x61 && b[3] === 0x43) {
      return 'audio/flac';
    }
  } catch (_) {
    // Ignore read errors; caller falls back to file.type
  }
  return file.type;
}

async function getOggDuration(file) {
  try {
    const headBuf = await file.slice(0, 4096).arrayBuffer();
    const head = new Uint8Array(headBuf);
    const headDV = new DataView(headBuf);

    let sampleRate = 48000;
    for (let i = 0; i < head.length - 16; i++) {
      if (head[i] === 79 && head[i + 1] === 112 && head[i + 2] === 117 && head[i + 3] === 115) {
        sampleRate = 48000;
        break;
      }
      if (head[i] === 1 && head[i + 1] === 118 && head[i + 2] === 111 && head[i + 3] === 114) {
        const rate = headDV.getUint32(i + 12, true);
        if (rate > 0) sampleRate = rate;
        break;
      }
    }

    const tailStart = Math.max(0, file.size - 65536);
    const tailBuf = await file.slice(tailStart).arrayBuffer();
    const tail = new Uint8Array(tailBuf);
    const tailDV = new DataView(tailBuf);

    for (let i = tail.length - 4; i >= 0; i--) {
      if (tail[i] === 79 && tail[i + 1] === 103 && tail[i + 2] === 103 && tail[i + 3] === 83) {
        if (i + 14 > tail.length) continue;
        const lo = tailDV.getUint32(i + 6, true);
        const hi = tailDV.getUint32(i + 10, true);
        if (lo === 0xffffffff && hi === 0xffffffff) continue;
        const granule = lo + hi * 4294967296;
        if (granule > 0) return Math.ceil(granule / sampleRate);
      }
    }
  } catch (_) {
    // Parsing failed; caller will fall back to the HTML audio element.
  }
  return null;
}

const readAudioDuration = (file) => new Promise((resolve, reject) => {
  const audio = document.createElement('audio');
  const objectUrl = URL.createObjectURL(file);
  let resolved = false;

  const cleanup = () => {
    URL.revokeObjectURL(objectUrl);
    audio.src = '';
  };

  audio.preload = 'metadata';
  audio.src = objectUrl;

  audio.onloadedmetadata = () => {
    if (resolved) return;
    resolved = true;
    const duration = Math.round(audio.duration);
    cleanup();
    if (!Number.isFinite(duration) || duration <= 0) {
      reject(new Error('Չհաջողվեց որոշել ֆայլի տևողությունը։'));
      return;
    }
    resolve(duration);
  };

  audio.onerror = () => {
    if (resolved) return;
    resolved = true;
    cleanup();
    reject(new Error('Չհաջողվեց կարդալ ֆայլը։'));
  };
});

const ChecklistUploadDialog = ({
  open,
  checklist,
  onClose,
  onAnalysisCompleted,
  metadataKeyOptions = [],
  metadataValuesByKey = {},
}) => {
  const [items, setItems] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [metadataEntries, setMetadataEntries] = useState([]);
  const [activeMetadataEntryId, setActiveMetadataEntryId] = useState('');
  const [forceMetadataValidationVisible, setForceMetadataValidationVisible] = useState(false);
  const [numSpeakers, setNumSpeakers] = useState('2');
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const itemsRef = useRef([]);
  const metadataEntryRefs = useRef(new Map());
  const metadataBlurTimeoutRef = useRef(null);
  const recordingUnsubsRef = useRef(new Map());
  const analysisUnsubsRef = useRef(new Map());
  const uploadTasksRef = useRef(new Map());
  const notifiedCompletedRef = useRef(new Set());
  const { userData } = useUserData();

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    if (!open) {
      setNumSpeakers('2');
    }
  }, [open]);

  useEffect(() => () => {
    if (metadataBlurTimeoutRef.current) {
      clearTimeout(metadataBlurTimeoutRef.current);
    }
    recordingUnsubsRef.current.forEach((unsubscribe) => unsubscribe());
    analysisUnsubsRef.current.forEach((unsubscribe) => unsubscribe());
  }, []);

  const activeItemsCount = useMemo(
    () => items.filter((item) => !isTerminalUploadState(item)).length,
    [items]
  );
  const isUploading = useMemo(
    () => items.some((item) => !isTerminalUploadState(item)),
    [items]
  );
  const resolvedNumSpeakers = useMemo(() => {
    const trimmed = String(numSpeakers).trim();
    if (trimmed === '') return null;
    const parsed = parseInt(trimmed, 10);
    if (!Number.isFinite(parsed)) return null;
    return Math.min(10, Math.max(1, parsed));
  }, [numSpeakers]);
  const metadataObject = useMemo(() => metadataEntries.reduce((acc, entry) => {
    const key = String(entry.key || '').trim();
    const value = String(entry.value || '').trim();
    if (!key || !value) return acc;
    acc[key] = value;
    return acc;
  }, {}), [metadataEntries]);
  const hasIncompleteMetadataEntry = useMemo(
    () => metadataEntries.some((entry) => {
      const key = String(entry.key || '').trim();
      const value = String(entry.value || '').trim();
      return Boolean(key || value) && (!key || !value);
    }),
    [metadataEntries]
  );
  const hasDuplicateMetadataKeys = useMemo(() => {
    const seen = new Set();
    return metadataEntries.some((entry) => {
      const key = String(entry.key || '').trim();
      if (!key) return false;
      const normalizedKey = key.toLowerCase();
      if (seen.has(normalizedKey)) return true;
      seen.add(normalizedKey);
      return false;
    });
  }, [metadataEntries]);
  const metadataBlockingMessage = hasIncompleteMetadataEntry
    ? 'Ավարտեք կամ հեռացրեք կիսատ մետատվյալների դաշտերը։'
    : hasDuplicateMetadataKeys
      ? 'Նույն մետատվյալների բանալին չի կարող կրկնվել։'
      : '';
  const metadataValidationMessage = metadataBlockingMessage
    && (forceMetadataValidationVisible || !activeMetadataEntryId)
    ? metadataBlockingMessage
    : '';

  const addMetadataEntry = () => {
    setForceMetadataValidationVisible(false);
    setMetadataEntries((prev) => [...prev, createMetadataEntry()]);
  };

  const updateMetadataEntry = (entryId, updates) => {
    setForceMetadataValidationVisible(false);
    setMetadataEntries((prev) => prev.map((entry) => (
      entry.id === entryId ? { ...entry, ...updates } : entry
    )));
  };

  const removeMetadataEntry = (entryId) => {
    setForceMetadataValidationVisible(false);
    setActiveMetadataEntryId((prev) => (prev === entryId ? '' : prev));
    setMetadataEntries((prev) => prev.filter((entry) => entry.id !== entryId));
  };

  const handleMetadataFieldFocus = (entryId) => {
    if (metadataBlurTimeoutRef.current) {
      clearTimeout(metadataBlurTimeoutRef.current);
      metadataBlurTimeoutRef.current = null;
    }
    setActiveMetadataEntryId(entryId);
    setForceMetadataValidationVisible(false);
  };

  const handleMetadataFieldBlur = (entryId) => {
    if (metadataBlurTimeoutRef.current) {
      clearTimeout(metadataBlurTimeoutRef.current);
    }
    metadataBlurTimeoutRef.current = window.setTimeout(() => {
      const entryNode = metadataEntryRefs.current.get(entryId);
      const activeElement = document.activeElement;
      if (entryNode && activeElement && entryNode.contains(activeElement)) {
        return;
      }
      setActiveMetadataEntryId((prev) => (prev === entryId ? '' : prev));
    }, 0);
  };

  const clearFinishedItems = () => {
    setItems((prev) => prev.filter((item) => !isTerminalUploadState(item)));
  };

  const updateItem = useCallback((clientId, updater) => {
    setItems((prev) => prev.map((item) => {
      if (item.id !== clientId) return item;
      return typeof updater === 'function' ? updater(item) : { ...item, ...updater };
    }));
  }, []);

  const releaseUploadResources = useCallback((clientId) => {
    const recordingUnsubscribe = recordingUnsubsRef.current.get(clientId);
    if (recordingUnsubscribe) {
      recordingUnsubscribe();
      recordingUnsubsRef.current.delete(clientId);
    }

    const analysisUnsubscribe = analysisUnsubsRef.current.get(clientId);
    if (analysisUnsubscribe) {
      analysisUnsubscribe();
      analysisUnsubsRef.current.delete(clientId);
    }

    uploadTasksRef.current.delete(clientId);
  }, []);

  const cancelItem = useCallback(async (clientId) => {
    const uploadTask = uploadTasksRef.current.get(clientId);
    if (uploadTask) {
      try {
        uploadTask.cancel();
      } catch (error) {
        console.error('Error cancelling upload task:', error);
      }
    }
    // If the task hasn't started uploading yet (still preparing), clean up manually
    if (!uploadTask) {
      const item = itemsRef.current.find((i) => i.id === clientId);
      if (item?.docId) {
        try {
          await deleteDoc(doc(db, 'recordings', item.docId));
        } catch (e) {
          console.error('Error deleting recording doc on cancel:', e);
        }
      }
      if (item?.analysisId) {
        try {
          await deleteDoc(doc(db, 'analyses', item.analysisId));
        } catch (e) {
          console.error('Error deleting analysis doc on cancel:', e);
        }
      }
      releaseUploadResources(clientId);
      updateItem(clientId, { phase: 'failed', error: 'Վերբեռնումը չեղարկվել է։' });
    }
  }, [releaseUploadResources, updateItem]);


  const attachRecordingListener = useCallback((clientId, recordingId) => {
    if (recordingUnsubsRef.current.has(clientId)) {
      recordingUnsubsRef.current.get(clientId)?.();
    }

    const unsubscribe = onSnapshot(
      doc(db, 'recordings', recordingId),
      (snapshot) => {
        if (!snapshot.exists()) {
          updateItem(clientId, (item) => {
            if (item.phase === 'completed' || item.phase === 'failed') {
              return item;
            }
            return {
              ...item,
              phase: 'failed',
              error: item.error || 'Ֆայլը հեռացվել է մշակման ընթացքում։ Հավանաբար հասանելի ժամանակը չի բավարարել։',
            };
          });
          releaseUploadResources(clientId);
          return;
        }

        const data = snapshot.data() || {};
        updateItem(clientId, (item) => ({
          ...item,
          phase: data.status === 'failed' ? 'failed' : item.phase,
          recordingStatus: data.status || item.recordingStatus,
          error: data.status === 'failed' ? data.error || item.error : item.error,
        }));

        if (data.status === 'failed') {
          releaseUploadResources(clientId);
        }
      },
      (error) => {
        console.error('Error listening to recording upload status:', error);
        updateItem(clientId, {
          phase: 'failed',
          error: 'Չհաջողվեց հետևել վերբեռնված ֆայլի կարգավիճակին։',
        });
      }
    );

    recordingUnsubsRef.current.set(clientId, unsubscribe);
  }, [releaseUploadResources, updateItem]);

  const attachAnalysisListener = useCallback((clientId, analysisId) => {
    if (analysisUnsubsRef.current.has(clientId)) {
      analysisUnsubsRef.current.get(clientId)?.();
    }

    const unsubscribe = onSnapshot(
      doc(db, 'analyses', analysisId),
      (snapshot) => {
        if (!snapshot.exists()) {
          return;
        }

        const data = snapshot.data() || {};

        updateItem(clientId, (item) => ({
          ...item,
          analysisStatus: data.status || item.analysisStatus,
          phase: data.status === 'completed'
            ? 'completed'
            : data.status === 'failed'
              ? 'failed'
              : item.phase,
          error: data.status === 'failed' ? data.error || item.error : item.error,
        }));

        if (data.status === 'completed' && !notifiedCompletedRef.current.has(clientId)) {
          notifiedCompletedRef.current.add(clientId);
          if (onAnalysisCompleted) {
            onAnalysisCompleted();
          }
        }

        if (data.status === 'completed' || data.status === 'failed') {
          releaseUploadResources(clientId);
        }
      },
      (error) => {
        console.error('Error listening to checklist analysis status:', error);
        updateItem(clientId, {
          phase: 'failed',
          error: 'Չհաջողվեց հետևել AI վերլուծության կարգավիճակին։',
        });
      }
    );

    analysisUnsubsRef.current.set(clientId, unsubscribe);
  }, [onAnalysisCompleted, releaseUploadResources, updateItem]);

  const startUploadForFile = useCallback(async (clientId, file, durationSeconds, metadata, effectiveType, numSpeakersValue) => {
    const user = auth.currentUser;
    if (!user || !checklist?.id) {
      updateItem(clientId, {
        phase: 'failed',
        error: 'Վերբեռնելու համար անհրաժեշտ է մուտք գործել և ընտրել ստուգացուցակ։',
      });
      return;
    }

    const recordingsRef = collection(db, 'recordings');
    const analysesRef = collection(db, 'analyses');
    const recordingRef = doc(recordingsRef);
    const analysisRef = doc(analysesRef);
    const recordingId = recordingRef.id;
    const fileType = effectiveType || file.type || 'audio/mpeg';
    const sizeMb = Number((file.size / (1024 * 1024)).toFixed(2));

    try {
      await setDoc(recordingRef, {
        userId: user.uid,
        status: 'uploading',
        createdAt: serverTimestamp(),
        duration: durationSeconds,
        fileType,
        source: 'upload',
        size: sizeMb,
        title: file.name,
        ...(numSpeakersValue ? { numSpeakers: numSpeakersValue } : {}),
        ...(Object.keys(metadata || {}).length > 0 ? { metadata } : {}),
      });

      await setDoc(analysisRef, {
        userId: user.uid,
        recordingId,
        checklistId: checklist.id,
        status: 'pending',
        createdAt: serverTimestamp(),
      });

      attachRecordingListener(clientId, recordingId);
      attachAnalysisListener(clientId, analysisRef.id);

      const storageRef = ref(storage, `recordings/${recordingId}-${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: fileType,
        customMetadata: { uid: user.uid, docId: recordingId },
      });
      uploadTasksRef.current.set(clientId, uploadTask);

      updateItem(clientId, {
        phase: 'uploading',
        progress: 0,
        docId: recordingId,
        analysisId: analysisRef.id,
      });

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          updateItem(clientId, {
            phase: 'uploading',
            progress: (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
          });
        },
        async (error) => {
          console.error('Checklist upload failed:', error);
          releaseUploadResources(clientId);

          try {
            await Promise.all([
              deleteDoc(recordingRef),
              deleteDoc(analysisRef),
            ]);
          } catch (cleanupError) {
            console.error('Error cleaning up failed checklist upload:', cleanupError);
          }

          updateItem(clientId, {
            phase: 'failed',
            error: error?.code === 'storage/canceled'
              ? 'Վերբեռնումը չեղարկվել է։'
              : 'Ֆայլի վերբեռնումը ձախողվեց։',
          });
        },
        () => {
          uploadTasksRef.current.delete(clientId);
          updateItem(clientId, {
            phase: 'uploaded',
            progress: 100,
          });
        }
      );
    } catch (error) {
      console.error('Error creating checklist upload records:', error);
      releaseUploadResources(clientId);
      updateItem(clientId, {
        phase: 'failed',
        error: 'Չհաջողվեց ստեղծել վերբեռնման գրառումը։',
      });
    }
  }, [attachAnalysisListener, attachRecordingListener, checklist?.id, releaseUploadResources, updateItem]);

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) {
      return;
    }
    if (metadataBlockingMessage) {
      setForceMetadataValidationVisible(true);
      return;
    }

    const newItems = files.map((file) => ({
      id: createClientUploadId(),
      name: file.name,
      sizeBytes: file.size,
      phase: 'preparing',
      progress: 0,
      durationSeconds: null,
      recordingStatus: '',
      analysisStatus: '',
      error: '',
      docId: '',
      analysisId: '',
    }));

    setItems((prev) => [...newItems, ...prev]);

    const activeReservedSeconds = itemsRef.current.reduce((sum, item) => {
      if (isTerminalUploadState(item)) {
        return sum;
      }
      return sum + Number(item.durationSeconds || 0);
    }, 0);

    const availableSecondsRaw = Number(userData?.remaining_seconds);
    let remainingBudget = Number.isFinite(availableSecondsRaw)
      ? Math.max(0, availableSecondsRaw - activeReservedSeconds)
      : Infinity;

    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      const clientId = newItems[index].id;

      if (file.type.startsWith('video/')) {
        updateItem(clientId, {
          phase: 'failed',
          error: 'Տեսանյութերը չեն աջակցվում։ Խնդրում ենք վերբեռնել միայն աուդիո ֆայլեր։',
        });
        continue;
      }

      try {
        // Detect real MIME type from magic bytes (handles M4A saved as .ogg, etc.)
        // eslint-disable-next-line no-await-in-loop
        const effectiveType = await detectAudioMimeType(file);
        const isOgg = effectiveType === 'audio/ogg';

        let durationSeconds;
        if (isOgg) {
          // Try fast binary parse first; fall back to HTML audio element
          // eslint-disable-next-line no-await-in-loop
          const oggDuration = await getOggDuration(file);
          if (oggDuration && oggDuration > 0) {
            durationSeconds = oggDuration;
          } else {
            // eslint-disable-next-line no-await-in-loop
            durationSeconds = await new Promise((resolve) => {
              const audio = document.createElement('audio');
              const objectUrl = URL.createObjectURL(file);
              const timer = setTimeout(() => {
                audio.src = '';
                URL.revokeObjectURL(objectUrl);
                resolve(Math.max(1, Math.ceil(file.size / 12000)));
              }, 8000);
              audio.preload = 'metadata';
              audio.src = objectUrl;
              audio.onloadedmetadata = () => {
                clearTimeout(timer);
                URL.revokeObjectURL(objectUrl);
                resolve(isFinite(audio.duration) ? Math.round(audio.duration) : Math.max(1, Math.ceil(file.size / 12000)));
              };
              audio.onerror = () => {
                clearTimeout(timer);
                URL.revokeObjectURL(objectUrl);
                resolve(Math.max(1, Math.ceil(file.size / 12000)));
              };
            });
          }
        } else {
          // eslint-disable-next-line no-await-in-loop
          durationSeconds = await readAudioDuration(file);
        }

        updateItem(clientId, { durationSeconds });

        if (Number.isFinite(remainingBudget) && durationSeconds > remainingBudget) {
          updateItem(clientId, {
            phase: 'insufficient_credits',
            durationSeconds,
            error: `Պահանջվում է ${formatDuration(durationSeconds)}, սակայն հասանելի ժամանակը բավարար չէ։`,
          });
          continue;
        }

        if (Number.isFinite(remainingBudget)) {
          remainingBudget -= durationSeconds;
        }

        // eslint-disable-next-line no-await-in-loop
        await startUploadForFile(clientId, file, durationSeconds, metadataObject, effectiveType, resolvedNumSpeakers);
      } catch (error) {
        console.error('Checklist upload file preparation failed:', error);
        updateItem(clientId, {
          phase: 'failed',
          error: error?.message || 'Չհաջողվեց պատրաստել ֆայլը վերբեռնման համար։',
        });
      }
    }
  }, [metadataBlockingMessage, metadataObject, resolvedNumSpeakers, startUploadForFile, updateItem, userData?.remaining_seconds]);

  useEffect(() => {
    if (!open) {
      setDragging(false);
      dragDepthRef.current = 0;
      return undefined;
    }

    const hasFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');

    const handleDragEnter = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setDragging(true);
    };

    const handleDragOver = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      setDragging(true);
    };

    const handleDragLeave = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) {
        setDragging(false);
      }
    };

    const handleDrop = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current = 0;
      setDragging(false);
      handleFiles(event.dataTransfer.files);
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
    };
  }, [handleFiles, open]);

  const selectedChecklistName = checklist?.name || 'ստուգացուցակ';

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' && dragging) {
            return;
          }
          onClose();
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1 }}>
          Ավելացնել ֆայլեր
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2}>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 3,
                backgroundColor: (theme) => (theme.palette.mode === 'light' ? '#fbfcff' : 'background.paper'),
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                spacing={1.5}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Խոսողների քանակ
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Թողեք դատարկ՝ ինքնաշխատ որոշման համար։
                  </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  {resolvedNumSpeakers === null && (
                    <Chip label="Ավտո" size="small" color="primary" variant="outlined" />
                  )}
                  <TextField
                    type="number"
                    size="small"
                    value={numSpeakers}
                    placeholder="Ավտո"
                    onChange={(event) => setNumSpeakers(event.target.value)}
                    onBlur={() => {
                      if (resolvedNumSpeakers === null) {
                        setNumSpeakers('');
                      } else {
                        setNumSpeakers(String(resolvedNumSpeakers));
                      }
                    }}
                    disabled={isUploading}
                    inputProps={{ min: 1, max: 10 }}
                    sx={{ width: { xs: '100%', sm: 120 } }}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 3,
                backgroundColor: (theme) => (theme.palette.mode === 'light' ? '#fbfcff' : 'background.paper'),
              }}
            >
              <Stack spacing={1.25}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Մետատվյալներ
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isUploading
                        ? 'Մետատվյալները հնարավոր չէ փոփոխել, քանի դեռ ֆայլերը մշակվում են։'
                        : 'Այս դաշտերը նույնությամբ կավելացվեն բոլոր ընտրված ֆայլերին։'}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={addMetadataEntry}
                    disabled={isUploading}
                    sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                  >
                    Ավելացնել դաշտ
                  </Button>
                </Stack>

                {metadataValidationMessage && (
                  <Alert severity="warning" sx={{ py: 0 }}>
                    {metadataValidationMessage}
                  </Alert>
                )}

                {metadataEntries.length > 0 && (
                  <Stack spacing={1}>
                    {metadataEntries.map((entry) => {
                      const selectedKey = String(entry.key || '').trim();
                      const valueOptions = selectedKey ? metadataValuesByKey[selectedKey] || [] : [];

                      return (
                        <Paper
                          key={entry.id}
                          ref={(node) => {
                            if (node) {
                              metadataEntryRefs.current.set(entry.id, node);
                            } else {
                              metadataEntryRefs.current.delete(entry.id);
                            }
                          }}
                          variant="outlined"
                          sx={{ p: 1, borderRadius: 2.5 }}
                        >
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1}
                            alignItems={{ xs: 'stretch', md: 'center' }}
                          >
                            <Autocomplete
                              freeSolo
                              disabled={isUploading}
                              options={metadataKeyOptions}
                              value={entry.key || ''}
                              onInputChange={(_, value, reason) => {
                                if (reason === 'reset') return;
                                updateMetadataEntry(entry.id, { key: value });
                              }}
                              onChange={(_, value) => {
                                updateMetadataEntry(entry.id, {
                                  key: typeof value === 'string' ? value : value || '',
                                  value: '',
                                });
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  label="Մետատվ. բանալի"
                                  placeholder="օր. բաժին"
                                  onFocus={() => handleMetadataFieldFocus(entry.id)}
                                  onBlur={() => handleMetadataFieldBlur(entry.id)}
                                />
                              )}
                              sx={{ minWidth: { xs: '100%', md: 220 }, flex: 1 }}
                            />

                            <Autocomplete
                              freeSolo
                              disabled={isUploading}
                              options={valueOptions}
                              value={entry.value || ''}
                              onInputChange={(_, value, reason) => {
                                if (reason === 'reset') return;
                                updateMetadataEntry(entry.id, { value });
                              }}
                              onChange={(_, value) => {
                                updateMetadataEntry(entry.id, {
                                  value: typeof value === 'string' ? value : value || '',
                                });
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  label="Արժեք"
                                  placeholder="օր. վաճառք"
                                  onFocus={() => handleMetadataFieldFocus(entry.id)}
                                  onBlur={() => handleMetadataFieldBlur(entry.id)}
                                />
                              )}
                              sx={{ minWidth: { xs: '100%', md: 240 }, flex: 1.1 }}
                            />

                            <Tooltip title="Հեռացնել մետատվյալը">
                              <span>
                                <IconButton
                                  onClick={() => removeMetadataEntry(entry.id)}
                                  disabled={isUploading}
                                  sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                borderStyle: 'dashed',
                borderWidth: 2,
                position: 'relative',
                borderColor: dragging ? 'primary.main' : 'divider',
                backgroundColor: dragging ? 'rgba(34, 158, 217, 0.08)' : 'rgba(34, 158, 217, 0.04)',
                textAlign: 'center',
              }}
            >
              {dragging && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(34, 158, 217, 0.12)',
                    borderRadius: 3,
                    pointerEvents: 'none',
                  }}
                >
                  <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700, px: 2 }}>
                    Բաց թողեք ֆայլերը վերբեռնելու համար
                  </Typography>
                </Box>
              )}
              <Stack spacing={1.5} alignItems="center">
                <CloudUploadIcon sx={{ fontSize: 52, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Քաշեք և թողեք ձեր աուդիո ֆայլերը այստեղ
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Կարող եք ընտրել միանգամից մի քանի ֆայլ։ Ֆայլերը ավտոմատ կվերլուծվեն ըստ այս ստուգացուցակի։
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  size="large"
                  startIcon={<CloudUploadIcon />}
                  disabled={Boolean(metadataBlockingMessage)}
                >
                  Ընտրել ֆայլեր
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept={ACCEPTED_AUDIO_TYPES}
                    onChange={(event) => {
                      handleFiles(event.target.files);
                      event.target.value = null;
                    }}
                  />
                </Button>
              </Stack>
            </Paper>

            {items.length > 0 && (
              <Stack spacing={1.25}>
                {items.map((item) => {
                  const presentation = resolveUploadPresentation(item);

                  return (
                    <Paper
                      key={item.id}
                      variant="outlined"
                      sx={{ p: 1.25, borderRadius: 2.5 }}
                    >
                      <Stack spacing={0.75}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={0.75}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            <InsertDriveFileOutlinedIcon color="action" sx={{ fontSize: 20 }} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }} noWrap>
                                {item.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatBytes(item.sizeBytes)}
                                {item.durationSeconds ? ` • ${formatDuration(item.durationSeconds)}` : ''}
                              </Typography>
                            </Box>
                          </Stack>

                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip size="small" label={presentation.label} color={presentation.color} />
                            {item.phase === 'uploading' && (
                              <Tooltip title="չևղարկվևլ վևրբևռնումը">
                                <IconButton
                                  size="small"
                                  onClick={() => cancelItem(item.id)}
                                  sx={{ color: 'text.secondary' }}
                                >
                                  <CancelOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>

                        <Typography
                          variant="caption"
                          color={presentation.color === 'error' ? 'error.main' : 'text.secondary'}
                          sx={{ lineHeight: 1.35 }}
                        >
                          {presentation.helper}
                        </Typography>

                        {presentation.progressVariant === 'determinate' && (
                          <LinearProgress
                            variant="determinate"
                            value={presentation.progressValue}
                            sx={{ height: 6, borderRadius: 999 }}
                          />
                        )}
                        {presentation.progressVariant === 'indeterminate' && (
                          <LinearProgress
                            variant="indeterminate"
                            sx={{ height: 6, borderRadius: 999 }}
                          />
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {activeItemsCount > 0
              ? `${activeItemsCount} ակտիվ վերբեռնում կամ մշակում`
              : items.length > 0
                ? 'Բոլոր վերբեռնումները կանգնած կամ ավարտված են'
                : 'Դեռ ֆայլեր չկան'}
          </Typography>

          <Stack direction="row" spacing={1}>
            {items.length > 0 && (
              <Button onClick={clearFinishedItems} disabled={!items.some((item) => isTerminalUploadState(item))}>
                Մաքրել ավարտվածները
              </Button>
            )}
            <Button onClick={onClose}>
              Փակել
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default ChecklistUploadDialog;
